import { useState, useRef, useEffect } from "react";
import { SignOut, CaretDown, Bell, ShieldCheck, Sun, Moon, Monitor, Database, UserPlus, GearSix, Plugs } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTheme, type ThemePreference } from "../contexts/ThemeContext";

const THEME_OPTIONS: { value: ThemePreference; icon: typeof Sun; label: string }[] = [
  { value: 'light', icon: Sun, label: 'Light' },
  { value: 'dark', icon: Moon, label: 'Dark' },
  { value: 'system', icon: Monitor, label: 'System' },
];

export default function UserMenu() {
  const { currentUser, userProfile, isModerator, logout } = useAuth();
  const { preference, setPreference } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleLogout = async () => {
    try {
      await logout();
      setIsOpen(false);
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  if (!currentUser) return null;

  const displayName = userProfile?.displayName || currentUser.displayName || currentUser.email || "User";

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
      >
        <div className="w-8 h-8 rounded-full border-2 border-gray-400 dark:border-gray-500 flex items-center justify-center text-gray-600 dark:text-gray-300 font-semibold text-sm">
          {displayName.charAt(0).toUpperCase()}
        </div>
        <span className="hidden sm:block text-sm font-medium text-gray-700 dark:text-gray-200">
          {displayName.split(" ")[0]}
        </span>
        <CaretDown size={16} className="text-gray-600 dark:text-gray-400" />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-50">
          <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
            <p className="text-sm font-medium text-gray-800 dark:text-gray-100">{displayName}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {currentUser.email}
            </p>
          </div>

          {/* Theme selector */}
          <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1.5">Theme</p>
            <div className="flex items-center gap-1">
              {THEME_OPTIONS.map(({ value, icon: Icon, label }) => (
                <button
                  key={value}
                  onClick={() => setPreference(value)}
                  title={label}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium transition-colors ${
                    preference === value
                      ? 'bg-pink-100 text-pink-700 dark:bg-pink-900 dark:text-pink-300'
                      : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'
                  }`}
                >
                  <Icon size={14} weight={preference === value ? 'fill' : 'regular'} />
                  <span>{label}</span>
                </button>
              ))}
            </div>
          </div>

          {isModerator && (
            <Link
              to="/admin"
              onClick={() => setIsOpen(false)}
              className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <ShieldCheck size={16} />
              <span>Admin Dashboard</span>
            </Link>
          )}
          <Link
            to="/settings/account"
            onClick={() => setIsOpen(false)}
            className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <GearSix size={16} />
            <span>Account Settings</span>
          </Link>
          <Link
            to="/settings/your-data"
            onClick={() => setIsOpen(false)}
            className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <Database size={16} />
            <span>Your Data</span>
          </Link>
          <Link
            to="/invite"
            onClick={() => setIsOpen(false)}
            className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <UserPlus size={16} />
            <span>Invite Friends</span>
          </Link>
          <Link
            to="/settings/notifications"
            onClick={() => setIsOpen(false)}
            className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <Bell size={16} />
            <span>Notification Settings</span>
          </Link>
          <Link
            to="/settings/integrations"
            onClick={() => setIsOpen(false)}
            className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <Plugs size={16} />
            <span>Integrations</span>
          </Link>
          <button
            onClick={handleLogout}
            className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <SignOut size={16} />
            <span>Sign Out</span>
          </button>
        </div>
      )}
    </div>
  );
}
